import { useLoaderData } from '@remix-run/react';
import type { LoaderFunctionArgs } from '@remix-run/node';
import type { Prisma } from '@prisma/client';
import DividedPill from './DividedPill';
import searchParamsSchema from './searchParamsSchema';
import MapDot from './MapDot';
import Image from '@/assets/images/map.png';
import db from '@/utils/db';
import PageWrapper from '@/components/app/PageWrapper';
import { Progress } from '@/components/ui/Progress';
import { RedDot } from '@/assets/icons';
import useRevalidateOnInterval from '@/hooks/useRevalidateOnInterval';
import { emojiOptions, getEmojiOption } from '@/utils/emojis';

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const { searchParams } = url;
  const donationsWhereClause: Prisma.DonationWhereInput = {
    name: {
      not: null, // Ensures that only donations with a name are returned
    },
  };
  const resultsWhereClause: Prisma.DonationWhereInput = {};
  const uniqueCountResultsWhereClause: Prisma.DonationWhereInput = {
    city: {
      not: null, // Ensures that only donations with a name are returned
    },
  };
  const campaign = searchParams.get('campaign');
  let goalInDollars = 18500000;

  // Add the campaign filtering only if a valid campaign is provided (not null or empty)
  if (campaign && campaign !== '') {
    donationsWhereClause.campaign = campaign;
    resultsWhereClause.campaign = campaign;
    uniqueCountResultsWhereClause.campaign = campaign;
    goalInDollars = 1500000;
  }

  const donations = await db.donation.findMany({
    where: donationsWhereClause, // Apply where clause only if campaign is provided
    distinct: ['name'], // Ensures that the names are unique
    orderBy: {
      id: 'desc', // Orders by `id` in descending order to get the most recent items
    },
    take: 100, // Limits the results to the last 100 entries
  });

  const sumResults = await db.donation.aggregate({
    where: resultsWhereClause, // Apply where clause for results based on campaign
    _sum: {
      amount_in_cents: true,
    },
  });

  const uniqueCountResults = await db.donation.findMany({
    where: uniqueCountResultsWhereClause,
    distinct: ['name'], // Get only unique names
    select: {
      name: true,
    },
  });
  const countResults = uniqueCountResults.length; // Count the unique records
  const results = {
    sum: sumResults._sum.amount_in_cents,
    count: countResults,
  };
  const getRandomEmoji = () => {
    const emojis = [...Object.values(emojiOptions)];
    return emojis[Math.floor(Math.random() * emojis.length)];
  };
  const customers = donations
    .filter(donation => !!donation.name)
    .map(
      donation =>
        donation.name!.toUpperCase()! +
        '  ' +
        `${donation.emoji ? ` ${getEmojiOption(donation.emoji)}` : getRandomEmoji()}`,
    );
  const cities = donations
    .map(donation => donation.city?.toUpperCase())
    .filter(city => city && city !== ''); // Excludes null, undefined, and empty strings
  const uniqueCities = Array.from(new Set(cities));
  const amountOfDonors = results.count ?? 0;
  const totalAmountRaisedInCents = results.sum ?? 0;
  const totalAmountInDollars = totalAmountRaisedInCents / 100;
  const amountOfGatherGroupsSupported = Math.floor(totalAmountInDollars / 100);
  const { interval, poll } = searchParamsSchema.parse({
    poll: searchParams.get('poll'),
    interval: searchParams.get('interval'),
  });
  const hasGoalBeenReached = totalAmountInDollars >= goalInDollars;

  return {
    customers,
    totalAmountInDollars,
    amountOfGatherGroupsSupported,
    amountOfDonors,
    goalInDollars: goalInDollars,
    uniqueCities,
    shouldPoll: poll,
    hasGoalBeenReached,
    pollingInterval: interval,
  };
};

const ResultsPage = () => {
  const {
    totalAmountInDollars,
    amountOfDonors,
    amountOfGatherGroupsSupported,
    customers,
    goalInDollars,
    shouldPoll,
    uniqueCities,
    hasGoalBeenReached,
    pollingInterval,
  } = useLoaderData<typeof loader>();

  useRevalidateOnInterval({ enabled: shouldPoll, interval: pollingInterval });

  return (
    <PageWrapper className="bg-[#252525] text-[#EEEDA8] overflow-hidden">
      <div className="w-full p-4 flex justify-end">
        <div className="font-general-sans-semibold text-[20px] text-[#252525] bg-[#EEEDA8] rounded-[70px] w-[117px] flex items-center justify-center">
          <div className="flex items-center justify-center">
            <RedDot className="-ml-4 animate-pulse" />
            <span>LIVE</span>
          </div>
        </div>
      </div>
      <div className="flex w-full">
        <div className="flex w-full flex-col xl:w-auto">
          <DividedPill name="Donors" value={amountOfDonors} />
          <DividedPill
            name="Gather Groups Supported"
            value={amountOfGatherGroupsSupported}
          />
          <DividedPill
            name={hasGoalBeenReached ? 'GOAL ACHIEVED!' : 'Donations'}
            value={totalAmountInDollars}
            isDollarAmount
          />
        </div>
        <div className="hidden xl:flex justify-center xl:w-full">
          <div className="relative flex">
            <img src={Image} alt="map" />
            <MapDot className="left-[100px] top-[85px]" />
            <MapDot className="left-[200px] top-[255px]" />
            <MapDot className="top-[70px] left-[410px]" />
            <MapDot className="top-[170px] left-[350px]" />
            <MapDot className="bottom-[40px] right-[290px]" />
            <MapDot className="right-[180px] top-[120px]" />
            <MapDot className="right-[40px] bottom-[160px]" />
          </div>
        </div>
      </div>
      <div className="w-full hidden from-[rgba(211,237,254,1)] to-[rgba(238,237,168,1)] bg-gradient-to-r text-[#252525] max-h-[304px] xl:flex flex-col justify-center p-4 overflow-hidden">
        <div className="grid row-span-full animate-marqueeResultsTop md:mt-10">
          {customers.map((customer, index) => (
            <p
              key={index}
              className="font-general-sans-medium text-2xl mx-20 row-span-full whitespace-nowrap break-words"
            >
              {customer}
            </p>
          ))}
        </div>
        <div className="relative flex px-16 mt-6">
          {totalAmountInDollars > 200000 && (
            <p className="absolute top-1/3 z-20 left-28 text-[#252525] font-general-sans-semibold text-4xl">
              {`$ ${totalAmountInDollars.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}`}
            </p>
          )}
          <Progress
            value={(totalAmountInDollars / goalInDollars) * 100}
            className="my-4"
          />
          <p className="absolute top-1/3 right-28 z-20 text-[#EEEDA8] font-general-sans-semibold text-4xl">
            {hasGoalBeenReached
              ? 'GOAL ACHIEVED!'
              : `$ ${goalInDollars.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}`}
          </p>
        </div>
        <div className="grid row-span-full animate-marqueeResultsBottom md:m-10">
          {uniqueCities.map((city, index) => (
            <p
              key={index}
              className="font-general-sans-medium text-2xl mx-20 row-span-full whitespace-nowrap"
            >
              {city}
            </p>
          ))}
        </div>
      </div>
    </PageWrapper>
  );
};

export default ResultsPage;
